import { useState } from "react";

import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Copy, Save, CheckCircle2, Loader2, Download, FileText as FilePdf, AlertTriangle, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/apiClient";
import FieldDictate from "./FieldDictate";
import SignaturePad from "./SignaturePad";

const ADMIN_FIELDS = [
  { key: "date_of_service", label: "Date of Service", ph: "YYYY-MM-DD" },
  { key: "service_name", label: "Service", ph: "" },
  { key: "type_of_contact", label: "Type of Contact", ph: "In-person" },
  { key: "place_of_service", label: "Place of Service", ph: "Community" },
  { key: "duration_minutes", label: "Duration (min)", ph: "60" },
  { key: "units", label: "Units (15 min)", ph: "4" },
  { key: "member_name", label: "Member Name", ph: "" },
  { key: "member_id", label: "Member ID / Record #", ph: "" },
  { key: "staff_name", label: "Staff Name", ph: "" },
  { key: "staff_credentials", label: "Credentials", ph: "QP" },
];

const NARRATIVE_FIELDS = [
  { key: "goals_addressed", label: "Targeted PCP Goal / Objective" },
  { key: "interventions", label: "Staff Interventions Provided (What Staff Did This Week)" },
  { key: "member_response", label: "Member Response" },
  { key: "progress", label: "Progress Toward Goal" },
  { key: "plan", label: "Plan for Next Week" },
];

function buildPlainText(s, showTs, createdAt) {
  const line = (l, v) => (v ? `${l}: ${v}` : `${l}:`);
  const parts = [
    "PSYCHOSOCIAL REHABILITATION (PSR) SERVICE NOTE",
    "",
    line("Date of Service", s.date_of_service),
    line("Service", s.service_name),
    line("Type of Contact", s.type_of_contact),
    line("Place of Service", s.place_of_service),
    line("Duration", s.duration_minutes ? `${s.duration_minutes} minutes` : ""),
    line("Units (15 min)", s.units),
    line("Member Name", s.member_name),
    line("Member ID / Record #", s.member_id),
    line("Staff", `${s.staff_name}${s.staff_credentials ? ", " + s.staff_credentials : ""}`),
    "",
    "GOAL(S) ADDRESSED (PCP):",
    s.goals_addressed,
    "",
    "INTERVENTIONS PROVIDED:",
    s.interventions,
    "",
    "MEMBER RESPONSE & EFFECTIVENESS:",
    s.member_response,
    "",
    "PROGRESS TOWARD GOAL:",
    s.progress,
    "",
    "PLAN / NEXT STEPS:",
    s.plan,
  ];
  if (showTs && createdAt) {
    parts.push("", `Documented: ${new Date(createdAt).toLocaleString()}`);
  }
  return parts.join("\n");
}

export default function NoteEditor({
  structured,
  setStructured,
  showTimestamp,
  setShowTimestamp,
  createdAt,
  onSave,
  saving,
  saved,
  signature,
  setSignature,
  staffName = "",
  staffCredential = "",
  weekOf = null,
  alignment = null,
  repeat = null,
  onRefreshInterventions = null,
  refreshing = false,
}) {
  const upd = (k) => (e) =>
    setStructured((s) => ({ ...s, [k]: e.target.value }));
  const appendField = (k) => (text) =>
    setStructured((s) => ({ ...s, [k]: s[k] ? s[k] + " " + text : text }));

  const copy = async () => {
    const text = buildPlainText(structured, showTimestamp, createdAt);
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard!");
    } catch (e) {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        toast.success("Copied to clipboard!");
      } catch (e2) {
        toast.error("Copy not permitted here. Use Export instead.");
      }
    }
  };

  const download = () => {
    const blob = new Blob([buildPlainText(structured, showTimestamp, createdAt)], {
      type: "text/plain",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `PSR-note-${structured.member_name || "member"}-${
      structured.date_of_service || "note"
    }.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Downloaded!");
  };

  const [pdfLoading, setPdfLoading] = useState(false);
  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const res = await api.post(
        "/notes/pdf",
        { structured, show_timestamp: showTimestamp, created_at: createdAt, signature, week_of: weekOf },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `PSR-${structured.member_name || "member"}-${structured.date_of_service || "note"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded!");
    } catch (e) {
      toast.error("Could not build PDF. Please try again.");
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="fade-up" data-testid="note-editor">
      {/* action bar */}
      <div className="-mx-6 lg:-mx-8 px-6 lg:px-8 py-4 mb-2 bg-white border-b border-[#E2DFD8] flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-xl font-bold text-[#121814] flex items-center gap-2">
            Your billable note
            {weekOf && (
              <span data-testid="weekly-badge" className="text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-md bg-[#C26D5C] text-white">
                Weekly · wk of {weekOf}
              </span>
            )}
          </h3>
          <p className="text-xs text-[#7A8C82]">Review, edit anything, then sign, save or download.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 mr-2 px-3 h-10 rounded-full bg-white border border-[#E2DFD8]">
            <Switch
              data-testid="timestamp-toggle"
              checked={showTimestamp}
              onCheckedChange={setShowTimestamp}
            />
            <span className="text-xs font-medium text-[#3A453F] whitespace-nowrap">Timestamp</span>
          </div>
          <Button
            data-testid="copy-note-btn"
            onClick={copy}
            variant="outline"
            className="h-10 rounded-full border-[#E2DFD8] bg-white hover:bg-[#EAE8E3]"
          >
            <Copy className="w-4 h-4 mr-2" strokeWidth={1.5} /> Copy
          </Button>
          <Button
            data-testid="download-note-btn"
            onClick={download}
            variant="outline"
            className="h-10 rounded-full border-[#E2DFD8] bg-white hover:bg-[#EAE8E3]"
          >
            <Download className="w-4 h-4 mr-2" strokeWidth={1.5} /> Export
          </Button>
          <Button
            data-testid="download-pdf-btn"
            onClick={downloadPdf}
            disabled={pdfLoading}
            variant="outline"
            className="h-10 rounded-full border-[#E2DFD8] bg-white hover:bg-[#EAE8E3]"
          >
            {pdfLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FilePdf className="w-4 h-4 mr-2" strokeWidth={1.5} /> PDF</>}
          </Button>
          <Button
            data-testid="save-note-btn"
            onClick={onSave}
            disabled={saving}
            className="h-10 rounded-full bg-[#4A6B53] hover:bg-[#3D5A45] text-white"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : saved ? (
              <>
                <CheckCircle2 className="w-4 h-4 mr-2" strokeWidth={1.5} /> Saved
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" strokeWidth={1.5} /> Save
              </>
            )}
          </Button>
        </div>
      </div>

      {/* goal-alignment warning */}
      {alignment && (alignment.status === "none" || alignment.status === "partial") && (
        <div
          data-testid="goal-alignment-warning"
          className={`mt-4 rounded-xl border p-4 flex items-start gap-3 fade-up ${
            alignment.status === "none" ? "bg-[#F6E4DF] border-[#e5c3ba]" : "bg-[#FBF3E6] border-[#ecdcb8]"
          }`}
        >
          <AlertTriangle className={`w-5 h-5 shrink-0 mt-0.5 ${alignment.status === "none" ? "text-[#C26D5C]" : "text-[#B98B2E]"}`} strokeWidth={1.5} />
          <div>
            <p className="text-sm font-bold text-[#121814]">
              {alignment.status === "none"
                ? "This work may not match a billable PSR goal"
                : "Loosely matches a PSR goal — double-check billability"}
            </p>
            <p className="text-xs text-[#5a5f5b] mt-0.5 leading-relaxed">
              {alignment.reason || "Make sure the note clearly ties to one of the consumer's PCP goals before billing."}
            </p>
          </div>
        </div>
      )}

      {/* weekly repeat guard */}
      {repeat?.repeat && (
        <div data-testid="repeat-warning" className="mt-4 rounded-xl border border-[#e5c3ba] bg-[#F6E4DF] p-4 fade-up">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5 text-[#C26D5C]" strokeWidth={1.5} />
            <div className="flex-1">
              <p className="text-sm font-bold text-[#121814]">These interventions match last week's note — interventions must change weekly</p>
              <p className="text-xs text-[#5a5f5b] mt-0.5 leading-relaxed">
                Saving is blocked until they differ. Last note ({repeat.previous?.date_of_service || "earlier"}): "{(repeat.previous?.interventions || "").slice(0, 180)}{(repeat.previous?.interventions || "").length > 180 ? "…" : ""}"
              </p>
              {onRefreshInterventions && (
                <Button data-testid="refresh-interventions-btn" onClick={onRefreshInterventions} disabled={refreshing} className="mt-3 h-10 rounded-full bg-[#C26D5C] hover:bg-[#a9553f] text-white">
                  {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" strokeWidth={1.5} />} Make it specific to this week
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* admin grid */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {ADMIN_FIELDS.map((f) => (
          <div key={f.key} className="space-y-1.5">
            <div className="flex items-center justify-between gap-1">
              <Label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">
                {f.label}
              </Label>
              <FieldDictate onText={appendField(f.key)} title={`Dictate ${f.label}`} />
            </div>
            <Input
              data-testid={`field-${f.key}`}
              value={structured[f.key] || ""}
              onChange={upd(f.key)}
              placeholder={f.ph}
              className="h-11 bg-[#F9F9F6] border-[#E2DFD8] text-sm"
            />
          </div>
        ))}
      </div>

      {/* narrative */}
      <div className="mt-6 space-y-5">
        {NARRATIVE_FIELDS.map((f) => (
          <div key={f.key} className="space-y-1.5">
            <div className="flex items-center justify-between gap-1">
              <Label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">
                {f.label}
              </Label>
              <FieldDictate onText={appendField(f.key)} title={`Dictate ${f.label}`} />
            </div>
            <Textarea
              data-testid={`field-${f.key}`}
              value={structured[f.key] || ""}
              onChange={upd(f.key)}
              rows={f.key === "interventions" ? 5 : 3}
              className="bg-[#F9F9F6] border-[#E2DFD8] text-sm leading-relaxed resize-y"
            />
          </div>
        ))}
      </div>

      {/* digital signature */}
      <div className="mt-8">
        <SignaturePad
          value={signature}
          onSign={setSignature}
          defaultName={staffName || structured.staff_name}
          defaultCredential={staffCredential || structured.staff_credentials}
        />
      </div>
    </div>
  );
}

export { buildPlainText };
